import { Col } from 'antd';
import { Link } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import SelectInput from '~src/shared/form/select/SelectInput';

import { fadeIn } from '~src/styles/animations/animations.sc';
import { media } from '~src/styles/media-templates';

export const headerHeight = 283;

const ratingSlideIn = keyframes`
  0% {
    margin-left: -211px;
    margin-top: -68px;
  }
  100% {
    margin-left: 0;
    margin-top: 0;
    width: auto;
  }
`;

export const HeaderContainer = styled.div<{ $smallHeader: boolean; $depth: number }>`
  padding-left: 120px;
  margin-left: -60px;
  background-image: linear-gradient(
    100deg,
    ${({ theme }) => theme.baseBlue.a10},
    ${({ theme }) => theme.violet.light}
  );
  min-height: 4rem;
  max-height: 18.867rem;
  width: 100%;
  height: ${headerHeight}px;
  position: fixed;
  @media print {
    position: relative;
  }
  z-index: ${({ $smallHeader }) => ($smallHeader ? '999' : '950')};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: height 0.03s;
  overflow: hidden;
  ${media.tablet`
    pointer-events: none;
  `}
  &:after {
    ${media.tablet`
      pointer-events: none;
    `};
    border-radius: inherit;
    background-image: linear-gradient(
      278deg,
      ${({ theme }) => theme.violet.medium},
      ${({ theme }) => theme.violet.light}
    );
    content: '';
    display: block;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    opacity: ${({ $depth }) => ($depth === 1 ? '1' : '0')};
    width: 100%;
    z-index: -100;
    transition: opacity 1.45s;
    overflow: hidden;
  }
  &:before {
    ${media.tablet`
      pointer-events: none;
    `};
    border-radius: inherit;
    background-image: linear-gradient(
      278deg,
      ${({ theme }) => theme.violet.dark},
      ${({ theme }) => theme.violet.medium}
    );
    content: '';
    display: block;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    opacity: ${({ $depth }) => ($depth === 2 ? '1' : '0')};
    width: 100%;
    z-index: -101;
    transition: opacity 1.45s;
    overflow: hidden;
  }
`;

export const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: -4.1rem;
  max-width: 1140px;
  height: 100%;
  width: 100%;
  max-height: ${headerHeight}px;
  position: relative;
`;

export const BreadCrumbsContainer = styled.div<{ $smallHeader: boolean }>`
  align-self: center;
  max-width: 650px;
  font-size: 12px;
  font-weight: 200;
  letter-spacing: 0.2px;
  color: rgba(255, 255, 255, 0.65);
  padding: 1.3335rem 0 ${({ $smallHeader }) => ($smallHeader ? '' : '0 0')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: all;
`;

export const HeaderAvatar = styled.div`
  box-shadow: 0 36px 30px 0 rgba(0, 0, 0, 0.14);
  border-radius: 50px;
  width: 80px;
  height: 80px;
  animation: ${fadeIn} 0.3s ease-in;
`;

export const HeaderRow = styled.div`
  gap: 8px;
  min-height: 42px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const ContentHeaderRow = styled(HeaderRow)`
  flex-grow: 1;
  height: 100%;
`;

export const HeaderCol = styled(Col)<{ $position?: string }>`
  justify-content: ${({ $position }) => $position || 'start'};
  height: 100%;
  display: flex;
  align-items: center;
  width: fit-content;
`;

export const AvatarBoxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  & h4 {
    margin: 0;
    color: white;
  }

  & h5 {
    font-size: 0.8rem;
    font-weight: 600;
    line-height: 1.83;
    letter-spacing: 0.2px;
    color: rgba(255, 255, 255, 1);
    opacity: 0.65;
    margin: 0;
    text-transform: uppercase;
  }
`;

export const ManagersBoxContainer = styled.div`
  display: flex;
  flex-direction: column;
`;


export const GraphColumn = styled(Col)<{ $isMcqScale?: boolean }>`
  overflow: ${({ $isMcqScale }) => ($isMcqScale ? 'auto' : 'none')};
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    width: 10px;
    background: rgba(0, 0, 0, 0.1);
  }
  &::-webkit-scrollbar-thumb {
    opacity: 0.2;
    background: rgba(0, 0, 0, 0.2);
    width: 2px;
  }
`;

export const InfoHeaderContainer = styled.div<{ $smallHeader: boolean }>`
  display: ${({ $smallHeader }) => ($smallHeader ? 'none' : 'flex')};
  height: 100%;
  width: 100%;
`;

export const CrumbLink = styled(Link)`
  color: ${({ theme }) => theme.white.a6};
  text-decoration: underline;
  &:hover {
    color: ${({ theme }) => theme.white.a6};
    text-decoration: underline;
  }
  &:focus {
    color: ${({ theme }) => theme.white.a6};
  }
`;

export const EmptyCrumbLink = styled.span`
  color: ${({ theme }) => theme.white.a6};
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const CrumbSeperator = styled.span`
  margin-left: 0.5rem;
  margin-right: 0.5rem;
`;

export const TopSection = styled.div`
  display: block;
  width: 100%;
`;

interface WithAnimation {
  $smallHeader?: boolean;
  $withAnimation: boolean;
  $withSlide?: boolean;
}

export const RatingContainer = styled.span<WithAnimation>`
  margin: ${({ $smallHeader }) => ($smallHeader ? '0' : '10px 0')};
  display: flex;
  font-size: ${({ $smallHeader }) => ($smallHeader ? '18px' : '25px')};
  text-align: center;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.white.a10};
  animation: ${({ $withSlide, $withAnimation }: WithAnimation) =>
    $withSlide && $withAnimation
      ? css`
          ${ratingSlideIn} 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)
        `
      : 'none'};
  line-height: 1;
`;

export const SecondNumber = styled.span<{ $smallHeader?: boolean }>`
  font-size: ${({ $smallHeader }) => ($smallHeader ? '15px' : '21px')};
  justify-self: start;
  margin-left: 5px;
`;

export const FirstNumber = styled.span<{ $smallHeader?: boolean }>`
  font-size: ${({ $smallHeader }) => ($smallHeader ? '18px' : '25px')};
  display: flex;
  justify-content: flex-end;
  width: ${({ $smallHeader }) => ($smallHeader ? '4rem' : '100%')};
  margin-right: 5px;
`;

export const PeriodSelectorContainer = styled.span<{ $smallHeader: boolean | undefined }>`
  display: flex;
  justify-content: center;
  animation: ${fadeIn} 0.3s ease-in;
  padding: 1.3335rem 0 ${({ $smallHeader }) => ($smallHeader ? '' : '0 0')};
  ${media.tablet`
    pointer-events: all;
  `}
`;

export const RatingInfoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

export const StyledTab = styled.div<{ $selected: boolean }>`
  flex: 1 1 0;
  background-color: rgba(237, 237, 237, ${({ $selected }) => ($selected ? '1' : '0.5')});
  &:hover {
    box-shadow: ${({ $selected }) => ($selected ? 'none' : 'inset 0px -30px 52px -47px rgba(0,0,0,0.75)')};
    background-color: ${({ theme }) => theme.background};
    color: ${({ theme }) => theme.baseBlue.a10};
  }
  border-radius: 5px 5px 0 0;
  height: 2.8rem;
  padding: 0.5rem;
  font-size: 12px;
  font-weight: 600;
  line-height: 22px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  color: ${({ $selected, theme }) => ($selected ? theme.baseBlue.a10 : theme.white.a10)};
  transition: all 0.5s ease;
`;

const tabsAnimation = keyframes`
  0% {
    margin-top: 50px;
  }
  100% {
    margin-top: 0;
  }
`;

export const TabsAnimationWrapper = styled.div`
  flex-shrink: 0;
  overflow: hidden;
  height: 2.8rem;
  @media print {
    display: none;
  }
`;

export const TabsContainer = styled.div<{ $noTabs: boolean }>`
  &.entered {
    display: ${({ $noTabs }) => ($noTabs ? 'none' : 'block')};
  }
  &.exited {
    display: none;
  }
  &.entering {
    animation: ${tabsAnimation} 150ms ease-in-out;
    animation-direction: initial;
  }
  &.exiting {
    animation: ${tabsAnimation} 150ms ease-in-out reverse;
  }

  align-items: end;
`;

export const CountInfoContainer = styled.div`
  text-align: center;
  justify-content: center;
  flex-direction: column;
  display: flex;
  font-size: 12px;
  font-weight: 200;
  letter-spacing: 0.2px;
  color: ${({ theme }) => theme.white.a6};
  padding: 0.3rem;
  animation: ${fadeIn} 0.3s ease-in;
`;

export const PeriodSelectorText = styled.span`
  text-align: center;
  margin-top: 5px;
`;

export const SmallHeaderRatingContainer = styled.div`
  padding: 1.3335rem 0;
`;

export const StyledSelectInput = styled(SelectInput)`
  & > div:first-child {
    margin-bottom: 0;
  }
`;
